import { isHiddenGame } from "@bing/shared";
import type { SessionStore } from "../store/session";
import { loadOnlineGame } from "./online-resume";

/**
 * A CRASH IS A LOST CONNECTION, NOT A «خروج» (card-a5 item 3 — operator, 2026-10-11: «When the app
 * crashes, the player's seat is given up at once. Treat it like a lost connection: the same grace
 * to come back.»)
 *
 * The root `ErrorBoundary` (`main.tsx`) catches a crash by replacing EVERYTHING under it with its
 * fallback, so the online table unmounts — and `OnlineGame`'s unmount cleanup used to close the
 * table's socket CONSENTED (`disconnect()`), which the game-server reads as the player LEAVING: the
 * chair to a bot at once (`leaveByClose`), the vacated seat counted for the partnership rule, a
 * private waiting room's HOST closing the table for everyone, the player `departed` from a private
 * table's front door. Nobody pressed anything that says «leave».
 *
 * So the boundary RAISES this latch the moment it catches a crash, and `OnlineGame`'s cleanup reads
 * it: a crash closes the socket with `drop()` — NON-consented, exactly as a dead socket closes — and
 * the server holds the seat for its drop grace and its reservation. Every other unmount (every
 * deliberate exit, every navigation) is untouched.
 *
 * ⚠️ THE ORDER IS THE WHOLE CLAIM. The boundary sets this in `componentDidCatch` — the LAYOUT phase of
 * the commit that shows the fallback — and the crashed subtree's `useEffect` cleanups, `OnlineGame`'s
 * included, run in that commit's PASSIVE phase, after it. `crash-keeps-seat.test.tsx` pins it through
 * real React, for every online game. Commit time ONLY, never `getDerivedStateFromError`: that one runs
 * in renders React may still throw away (it retries a failed render once and, when the retry
 * succeeds, commits the children — then nothing unmounted, and a latch raised there would linger).
 *
 * ⚠️ AND IT MUST NOT OUTLIVE THE CRASH. A latch still up when a LATER table unmounts would turn a
 * deliberate exit into a drop. It is lowered when the boundary hands the screen back
 * ({@link crashReturn}'s door) and whenever a table mounts — a table that mounts is a fresh start.
 */
let crashed = false;

/** The boundary caught a crash: the table now unmounting is a lost connection, not an exit. */
export function markAppCrash(): void {
  crashed = true;
}

/** Is the unmount happening right now a crash's? Read by `OnlineGame`'s cleanup. */
export function appCrashed(): boolean {
  return crashed;
}

/** The crash is over — the boundary handed the screen back, or a table mounted afresh. */
export function clearAppCrash(): void {
  crashed = false;
}

/**
 * WHAT THE MOUNTED `OnlineGame` IS SITTING AT — the session's `tableOnScreen`, read by the crash screen.
 * `roomId` / `token`: the seat as the connector last reported it (`onLive` — in memory only, never the
 * strip's ticket), else the join intent's room (a `join` / `rejoin`) and token (a `rejoin`) while it is
 * still connecting; null when nothing names the room yet (a quick-match still connecting).
 */
export interface TableOnScreen {
  readonly game: string;
  readonly roomId: string | null;
  readonly token: string | null;
  /** The table has dealt (playing, or a finished match). */
  readonly dealt: boolean;
  /** The server holds this seat after a drop — false ONLY for a public waiting room this player sat in
   *  alone: the game-server frees that seat at the drop (`leaveLobby`), there is nothing to return to. */
  readonly seatHeld: boolean;
}

/** The crash screen's door back to the table — see {@link crashReturn}. */
export interface TableReturn {
  /** The ticket is a PRIVATE waiting room that has not dealt (`SavedOnlineGame.pending`): the
   *  button says «بازگشت به میز», not «بازگشت به بازی» — the maintenance door's own wording. */
  readonly pending: boolean;
  /** Re-enter it: `rejoinOnline` with this device's ticket (`client.reconnect(token)`). */
  readonly go: () => void;
}

/**
 * THE WAY BACK TO THE TABLE, for the crash screen — or null when there is none.
 *
 * The same door the maintenance screen and the lobby's «ادامه بازی» strip open — `rejoinOnline`, i.e.
 * `client.reconnect(token)` into the reservation the crash's drop made the server hold — a BUTTON,
 * never an automatic return; a game hidden from players is never offered back. Which table:
 *
 *  · A TABLE WAS ON SCREEN (`tableOnScreen`) → that table, or none. Its seat in memory, so a PUBLIC
 *    waiting room — which keeps no ticket (the save gate's rule) and whose seat the server holds for
 *    120 s once another human sits there — has its door too; while still connecting, the intent's
 *    room (a `rejoin` brings its token; a link `join` opens only with that room's own ticket). Never an
 *    OLDER ticket's table — a game the player left earlier with «خروج», still reclaimable — which a
 *    button saying «بازگشت…» right after the crash would otherwise walk them into. Nothing when the
 *    room is not known yet (a quick-match still connecting) or the server freed the seat (a public
 *    waiting room the player sat in alone).
 *  · NO TABLE ON SCREEN (a crash in the lobby, any other tab) → this device's ticket, as the strip
 *    offers it.
 *
 * Asked when the fallback renders, never earlier. Never throws — a fallback that can fail is no
 * fallback.
 */
export function crashReturn(store: SessionStore): TableReturn | null {
  try {
    const rejoin =
      (game: string, roomId: string, token: string): (() => void) =>
      () =>
        store.getState().rejoinOnline(game, roomId, token);
    const saved = loadOnlineGame();
    const table = store.getState().tableOnScreen?.() ?? null;
    if (table === null) {
      if (!saved || isHiddenGame(saved.game)) return null;
      return {
        pending: saved.pending === true,
        go: rejoin(saved.game, saved.roomId, saved.token),
      };
    }
    if (isHiddenGame(table.game) || table.roomId === null || !table.seatHeld) return null;
    const ticket = saved?.roomId === table.roomId ? saved : null;
    const token = table.token ?? ticket?.token ?? null;
    if (token === null) return null;
    return {
      // The ticket of the SAME table knows whether it dealt even while it is still reconnecting.
      pending: ticket ? ticket.pending === true : !table.dealt,
      go: rejoin(table.game, table.roomId, token),
    };
  } catch {
    return null;
  }
}
